import { create } from "zustand";
import axios from "axios";

// Create authenticated axios instance
const authAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// Request interceptor to add auth token
authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  const tenantId = localStorage.getItem("tenantId");
  const role = localStorage.getItem("role");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (tenantId) {
    config.params = config.params || {};
    config.params.tenantId = tenantId;
    if (role === "SuperAdmin" && tenantId) {
      config.params.allTenants = true; // SuperAdmin can bypass tenancy
    }
  }
  return config;
});

// Response interceptor to handle errors
authAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error("Authentication failed - redirecting to login");
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

export const useUploadStore = create((set, get) => ({
  data: [],
  videoFile: null,
  uploadProgress: 0,
  isLoading: false,
  error: null,

  // Set video file for upload
  setVideoFile: (file) => set({ videoFile: file }),

  // Update upload progress
  setUploadProgress: (progress) => set({ uploadProgress: progress }),

  // Clear error state
  clearError: () => set({ error: null }),

  // Fetch video data for a specific survey
  fetchVideoData: async (surveyId) => {
    set({ isLoading: true, error: null, data: [] }); // Reset data to ensure fresh fetch
    try {
      const response = await authAxios.get(`api/videos/getvideos/${surveyId}`);
      const videos = response.data.videos || response.data || [];
      set({ data: videos, isLoading: false });
      return videos;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch video data";
      set({ error: errorMessage, isLoading: false, data: [] });
      console.error("Error fetching video data:", error);
      throw error;
    }
  },

  // Handle video upload with progress tracking
  handleUpload: async (surveyId, gpxFile, videoLabel) => {
    const { videoFile, setUploadProgress, setVideoFile } = get();

    if (!videoFile) {
      set({ error: "Please select or record a video to upload" });
      return;
    }

    set({ isLoading: true, error: null, uploadProgress: 0 });

    const formData = new FormData();
    formData.append("video", videoFile);
    formData.append("gpx", gpxFile);
    formData.append("surveyId", surveyId);
    formData.append("videoLabel", videoLabel);
    const role = localStorage.getItem("role");
    const tenantId = localStorage.getItem("tenantId");
    if (role === "SuperAdmin") {
      formData.append("tenantId", null); // Explicitly send null for SuperAdmins
    } else if (tenantId) {
      formData.append("tenantId", tenantId); // Send tenantId for non-SuperAdmins
    } else {
      set({ error: "tenantId is required for non-SuperAdmin users", isLoading: false });
      return;
    }

    try {
      const response = await authAxios.post("api/videos/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1)
          );
          set({ uploadProgress: progress });
        },
      });

      set({
        isLoading: false,
        uploadProgress: 100,
        videoFile: null,
      });

      // Refresh video data after successful upload
      await get().fetchVideoData(surveyId);

      return {
        success: true,
        data: response.data,
        message: "Upload successful!",
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Error uploading video";
      set({
        error: errorMessage,
        isLoading: false,
        uploadProgress: 0,
      });
      console.error("Error uploading video:", error);
      throw error;
    }
  },

  // Cancel ongoing upload
  cancelUpload: () => {
    // This would need integration with axios cancel token in a real implementation
    set({
      uploadProgress: 0,
      isLoading: false,
      error: "Upload cancelled",
    });
  },
}));
